import React, { Fragment, useMemo, useState } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { useAppDispatch, useAppSelector } from 'src/store'

// Selectors
import { selectCountMessageRead } from 'src/store/domain/chat/message/selector'
// Constants
import { MESSAGE_TYPE } from 'src/containers/Chat/constants'
import { deleteMessageRequest } from 'src/useCase/message'
import ConfirmDeleteMessageModal from 'src/components/Modal/ConfirmDeleteMessageModal'
import { actions } from 'src/store/domain/chat/message'
import { ReactComponent as VerticalDotsIcon } from 'src/assets/icons/vertical_dots_icon.svg'

type Props = {
  item: {
    id: number
    type_message: number
    message_time: string
    image_url: string
    message_content: string
    created_at: string
    is_temporary: boolean
  }
}

const MyMessage = (props: Props) => {
  const { item } = props
  const {
    id,
    type_message,
    message_content,
    image_url,
    created_at,
    is_temporary,
  } = item
  const countMessageRead = useAppSelector(selectCountMessageRead)
  const [isHovered, setIsHovered] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const dispatch = useAppDispatch()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const enableMessageAction = false

  const isImage = useMemo(() => {
    if (!id) {
      return false
    }
    return type_message === MESSAGE_TYPE.IMAGE
  }, [id, type_message])

  const messageTime = useMemo(() => {
    moment.locale('ja')
    let startDate = moment(created_at).startOf('day')
    let time = moment(created_at)
    if (moment().diff(startDate, 'days') == 0) {
      return time.format('HH:mm')
    }
    return time.format('M/DD HH:mm')
  }, [created_at])

  const handleEdit = () => {
    dispatch(actions.setEditingMessage({ ...item, key: String(item.id) }))
    setShowMenu(false)
  }

  const handleDelete = () => {
    dispatch(deleteMessageRequest({ id: item.id }))
    setShowDeleteModal(false)
  }

  return (
    <Wrapper>
      <div
        className="my-message"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false)
          setShowMenu(false)
        }}
      >
        <Fragment>
          {isImage ? (
            <div className="my-message__message">
              <img src={image_url} />
            </div>
          ) : (
            <div className="my-message__message">
              <span dangerouslySetInnerHTML={{ __html: message_content }} />
              {isHovered && enableMessageAction && (
                <>
                  <div
                    className="my-message__action-button"
                    onClick={() => setShowMenu(!showMenu)}
                  >
                    <VerticalDotsIcon />
                  </div>
                  {showMenu && (
                    <div className="my-message__menu">
                      <div
                        className="my-message__menu-item"
                        onClick={handleEdit}
                      >
                        編集
                      </div>
                      <div
                        className="my-message__menu-item"
                        onClick={() => setShowDeleteModal(true)}
                      >
                        削除
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          <div className="my-message__message-time">
            <div
              className={
                countMessageRead >= item.id
                  ? 'my-message__message-time__text'
                  : 'my-message__hidden'
              }
            >
              既読
            </div>
            <div>{is_temporary ? '送信中...' : messageTime}</div>
          </div>
        </Fragment>
      </div>
      <ConfirmDeleteMessageModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
      />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  .my-message {
    margin-bottom: 20px;
    display: flex;
    justify-content: flex-end;
    position: relative;
    align-items: flex-end;
    flex-direction: column;

    &__hidden {
      visibility: hidden;
    }

    &__message-time {
      font-size: 12px;
      color: #989898;
      display: flex;
      gap: 10px;

      &__text {
        text-align: right;
      }
    }

    &__message {
      margin-left: 8px;
      background: #f1efe7;
      border: 1px solid #998800;
      padding: 10px 8px;
      border-radius: 10px;
      max-width: 75%;
      word-break: break-word;
      font-size: 14px;
      white-space: pre-line;
      position: relative;
      display: inline-block;
      width: fit-content;
    }

    &__action-button {
      position: absolute;
      left: -35px;
      top: 50%;
      transform: translateY(-50%);
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      font-size: 18px;
      color: #333;
    }

    &__menu {
      position: absolute;
      left: -125px;
      top: 50%;
      transform: translateY(-50%);
      background-color: #d9d9d9;
      border-radius: 5px;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
      padding: 5px 0;
      min-width: 80px;
      z-index: 999;
      text-align: center;

      &::before {
        content: '';
        position: absolute;
        right: -16px;
        top: 50%;
        transform: translateY(-50%);
        border-width: 8px;
        border-style: solid;
        border-color: transparent transparent transparent #d9d9d9;
      }
    }

    &__menu-item {
      padding: 5px 8px;
      cursor: pointer;
      font-size: 14px;
      color: #333;
      white-space: nowrap;

      &:hover {
        background-color: #c0c0c0;
      }
    }
  }
`

export default MyMessage
